<template>
    <div class="search-page">
        <v-window :model-value="page" :touch="false" class="search-page__inner">
            <v-window-item value="0" :transition="!isMobile">
                <div class="search-page__header">
                    <div class="search-page__header-content pt-md-6 px-md-6">
                        <search-mode-toggle :model-value="searchMode" :query="query" class="search-page__header-top" />
                        <div class="search-page__header-bottom pa-4">
                            <router-view name="header" />
                        </div>
                    </div>
                    <div v-show="showHeaderAppend" class="search-page__header-append">
                        <router-view name="headerAppend" />
                    </div>
                </div>

                <div class="search-page__content px-4 pb-4 pt-md-4 px-md-6">
                    <router-view />
                </div>
            </v-window-item>
            <v-window-item value="1" :transition="!isMobile">
                <router-view name="page1" />
            </v-window-item>
        </v-window>
    </div>
</template>

<script setup lang="ts">
import { SearchModeToggle } from '@/features/common/components';
import { SearchPageProvideKey } from '@/injectionKeys';
import { provide, ref, watch } from 'vue';
import { useDisplay } from 'vuetify';
import { usePlacesStore } from '@/features/places/stores';
import { storeToRefs } from 'pinia';
import { SearchMode, useMainStore } from '@/features/common/stores';
import { PlaceType } from '@/types/webapi';
import { useRouter } from 'vue-router';
import { isEmpty } from 'lodash-es';
import { useTripSearchStore } from '@/features/trips/stores';
import { useDeparturesSearchStore } from '@/features/departures/stores';
import { useFromToPlaces } from '@/features/places/composables';

const { searchMode } = storeToRefs(useMainStore());
const placesStore = usePlacesStore();
const tripStore = useTripSearchStore();
const departuresStore = useDeparturesSearchStore();
const { fromPlace } = storeToRefs(placesStore);
const { mobile: isMobile } = useDisplay();
const router = useRouter();
useFromToPlaces();

const query = (mode: SearchMode) => {
    // if current searchMode equals mode, return undefined
    // else if fromPlace is a transit station, return the fromPlace id
    // else return undefined
    return {
        fromId: searchMode.value === mode ? undefined : fromPlace.value?.type === PlaceType.TransitStation ? fromPlace.value?.id : undefined
    };
};

const showHeader = ref(true);
const showHeaderAppend = ref(false);
const page = ref(0);

function setHeaderVisibility(value: boolean) {
    showHeader.value = value;
}

function setHeaderAppendVisibility(value: boolean) {
    showHeaderAppend.value = value;
}

function setPage(value = 0) {
    page.value = value;
}

router.afterEach(to => {
    if (isEmpty(to.query)) {
        placesStore.$reset();
        tripStore.$reset();
        departuresStore.$reset();
    }
});

provide(SearchPageProvideKey, { setHeaderVisibility, setHeaderAppendVisibility, setPage });

watch(isMobile, value => {
    if (!value) {
        setPage(0);
    }
});
</script>

<style lang="scss">
@use 'vuetify/settings' as *;
@use 'sass:map';

.search-page {
    --v-search-page-border-color: #e6e6e6;

    width: 100%;
    height: 100%;

    &__inner {
        height: 100%;
    }

    .v-window-item {
        min-height: 100%;

        &--active {
            overflow-y: overlay;

            &:has(.search-result) {
                --scrollbar-color-track: var(--v-color-grey-lighten-3);
            }
        }
    }

    &__header {
        z-index: 1;

        &-top,
        &-bottom {
            position: relative;
        }

        &-bottom {
            @media #{map.get($display-breakpoints, 'md-and-up')} {
                border: 1px solid var(--v-search-page-border-color);
                border-radius: #{map.get($rounded, 'lg')};
                border-top-left-radius: 0;
            }
        }

        &-content,
        &-append {
            position: relative;
        }

        &-content {
            z-index: 2;
        }

        &-append {
            z-index: 1;
        }
    }

    .search-mode-toggle__container {
        position: relative;
        bottom: -1px;
        // offset with margin so we don't end up with empty space at top
        margin-top: -1px;
    }

    &:has(.v-autocomplete--active-menu.place-autocomplete) .search-page__content {
        min-height: 500px;
    }
}

.search-result {
    display: flex;
    flex-direction: column;
    min-height: 100%;
    background: var(--search-result-bg-color);

    &__header {
        display: flex;

        @media #{map.get($display-breakpoints, 'md-and-up')} {
            min-height: 100px;
            box-shadow:
                0px 1px 4px 0px rgba(0, 0, 0, 0.1),
                0px 1px 2px 0px rgba(0, 0, 0, 0.12);
        }
    }

    &--mobile {
        background-color: transparent;

        .search-result__header,
        .search-result__footer {
            display: flex;
        }

        .search-result__footer {
            flex-direction: column;
            justify-content: center;
            background: rgb(var(--v-theme-surface));
        }

        &.search-result--expanded {
            background: var(--v-color-grey-lighten-3);

            .search-result__content {
                padding-bottom: 80px;

                .tp-app--has-focused & {
                    padding-bottom: 20px;
                }
            }

            .search-result__footer {
                position: fixed;
                width: 100%;
                bottom: 0;
                z-index: 100;

                .tp-app--has-focused & {
                    position: sticky;
                }
            }
        }
    }
}
</style>
